// Comms: the company chat (channels + DMs) and everyone's mail. Admins can
// post into a channel as "the owner".
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../../convex/_generated/api';
import type { Doc } from '../../convex/_generated/dataModel';
import { EmailList } from '../components/Email';
import { fmtClock, fmtDay, shortName } from '../lib/format';
import { useCast, useNow, type Cast } from '../lib/hooks';
import { useSession, useSimMutation, useSimQuery } from '../lib/session';
import { Avatar } from '../ui/Avatar';
import { Icon } from '../ui/Icon';
import { Button, Empty, Panel, Segmented, Spinner } from '../ui/primitives';
import { useToasts } from '../ui/Toasts';

type Tab = 'chat' | 'mail';

export function CommsView() {
  const [tab, setTab] = useState<Tab>('chat');
  return (
    <div className="page comms-page">
      <header className="page-head row">
        <div>
          <h1>Comms</h1>
          <p>Company chat and every inbox. aa-app alert emails land here too.</p>
        </div>
        <Segmented<Tab>
          ariaLabel="Comms"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'chat', label: 'Chat', icon: 'chat' },
            { value: 'mail', label: 'Mail', icon: 'mail' },
          ]}
        />
      </header>
      {tab === 'chat' ? <Chat /> : <Mail />}
    </div>
  );
}

/** The owner's DM with a persona is channel `owner:<personaKey>`; it exists after the first message. */
const OWNER_DM = 'owner:';

function Chat() {
  const cast = useCast();
  const { isAdmin } = useSession();
  const channels = useSimQuery(api.comms.chat.listChannels, {});
  const [channelKey, setChannelKey] = useState<string>('general');
  const messages = useSimQuery(api.comms.chat.listMessages, { channelKey, limit: 100 });
  const now = useNow(30_000);
  const sorted = useMemo(() => [...(messages ?? [])].sort((a, b) => a.at - b.at), [messages]);
  const end = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [sorted.length]);

  const publicChannels = channels?.filter((c) => c.kind === 'public') ?? [];
  const dms = channels?.filter((c) => c.kind === 'dm') ?? [];
  const current = channels?.find((c) => c.key === channelKey);
  const dmPersona = channelKey.startsWith(OWNER_DM) ? cast?.byKey.get(channelKey.slice(OWNER_DM.length)) : undefined;
  const title = current?.name ?? (dmPersona ? `Owner ↔ ${shortName(dmPersona.name)}` : 'Chat');
  const dmTargets = cast?.cards.filter((c) => !channels?.some((ch) => ch.key === `${OWNER_DM}${c.key}`)) ?? [];

  return (
    <div className="chat">
      <nav className="chat-channels panel" aria-label="Channels">
        <h3>Channels</h3>
        {channels === undefined ? <Spinner /> : null}
        {publicChannels.map((c) => (
          <button key={c._id} type="button" className={c.key === channelKey ? 'is-active' : ''} onClick={() => setChannelKey(c.key)}>
            <Icon name="chat" size={15} />
            {c.name}
          </button>
        ))}
        {dms.length > 0 ? <h3>Direct messages</h3> : null}
        {dms.map((c) => (
          <button key={c._id} type="button" className={c.key === channelKey ? 'is-active' : ''} onClick={() => setChannelKey(c.key)}>
            <span className="dm-faces">
              {c.memberPersonaIds.slice(0, 2).map((id) => {
                const p = cast?.byId.get(id);
                return p ? <Avatar key={id} personaKey={p.key} size={18} /> : null;
              })}
            </span>
            {c.key.startsWith(OWNER_DM) ? c.name : c.memberPersonaIds.map((id) => cast?.nameOf(id)).join(' & ') || c.name}
          </button>
        ))}
        {isAdmin && dmTargets.length > 0 ? (
          <label className="dm-new">
            <span>Message someone as the owner</span>
            <select
              value={dmPersona && !current ? dmPersona.key : ''}
              onChange={(e) => {
                if (e.target.value) setChannelKey(`${OWNER_DM}${e.target.value}`);
              }}
            >
              <option value="">Choose a person</option>
              {dmTargets.map((c) => (
                <option key={c._id} value={c.key}>
                  {shortName(c.name)} · {c.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </nav>
      <Panel className="chat-room" title={title} icon="chat" actions={current?.topic ? <span className="muted">{current.topic}</span> : undefined}>
        <div className="chat-scroll">
          {messages === undefined ? <Spinner /> : null}
          {messages?.length === 0 ? (
            <Empty icon="chat" title={dmPersona ? 'A new conversation' : 'Crickets'}>
              {dmPersona ? `${shortName(dmPersona.name)} reads this the next time they check their messages, and may answer.` : 'Nobody has posted here yet.'}
            </Empty>
          ) : null}
          <MessageList messages={sorted} cast={cast} now={now} />
          <div ref={end} />
        </div>
        <Composer channelKey={channelKey} />
      </Panel>
    </div>
  );
}

function MessageList({ messages, cast }: { messages: Doc<'messages'>[]; cast: Cast | undefined; now: number }) {
  let lastDay = '';
  return (
    <ol className="messages">
      {messages.map((m) => {
        const day = fmtDay(m.at);
        const divider = day !== lastDay;
        lastDay = day;
        const persona = m.author.kind === 'persona' ? cast?.byId.get(m.author.personaId) : undefined;
        const name = m.author.kind === 'owner' ? 'The owner' : m.author.kind === 'system' ? 'Volt & Vine bot' : persona ? shortName(persona.name) : 'Someone';
        const replyTo = m.replyToId ? messages.find((x) => x._id === m.replyToId) : undefined;
        return (
          <li key={m._id} className={`message author-${m.author.kind}`}>
            {divider ? <div className="day-divider">{day}</div> : null}
            <div className="message-row">
              {persona ? (
                <Link href={`/agent/${persona.key}`}>
                  <Avatar personaKey={persona.key} mood={persona.mood} size={34} />
                </Link>
              ) : (
                <span className={`message-badge kind-${m.author.kind}`}>
                  <Icon name={m.author.kind === 'owner' ? 'key' : 'bolt'} size={16} />
                </span>
              )}
              <div className="message-body">
                <div className="message-meta">
                  <b>{name}</b>
                  <time>{fmtClock(m.at)}</time>
                </div>
                {replyTo ? <p className="message-reply">↳ {replyTo.text.slice(0, 90)}</p> : null}
                <p className="message-text">{m.text}</p>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Composer({ channelKey }: { channelKey: string }) {
  const { isAdmin } = useSession();
  const post = useSimMutation(api.comms.chat.postAsOwner);
  const toasts = useToasts();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return <p className="composer-note">Only the owner (admin passcode) can post here.</p>;
  return (
    <form
      className="composer"
      onSubmit={async (e) => {
        e.preventDefault();
        const t = text.trim();
        if (!t) return;
        setBusy(true);
        try {
          // The owner's DM channel is created by the first message (postAsOwner's `dm:<key>` form).
          await post({ channelKey: channelKey.startsWith(OWNER_DM) ? `dm:${channelKey.slice(OWNER_DM.length)}` : channelKey, text: t });
          setText('');
        } catch (err) {
          toasts.push(err instanceof Error ? err.message : 'Message not sent.', { tone: 'danger', icon: 'alert' });
        } finally {
          setBusy(false);
        }
      }}
    >
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Say something as the owner" maxLength={1000} aria-label="Message" />
      <Button type="submit" tone="sky" icon="send" disabled={busy || !text.trim()}>
        Send
      </Button>
    </form>
  );
}

function Mail() {
  const cast = useCast();
  const now = useNow(30_000);
  const [who, setWho] = useState<string>('all');
  const personaId = who === 'all' ? undefined : cast?.byKey.get(who)?._id;
  const emails = useSimQuery(api.comms.mail.listEmails, who === 'all' || personaId ? { personaId, limit: 60 } : 'skip');
  return (
    <div className="mail">
      <div className="mail-filter" role="tablist" aria-label="Inbox">
        <button type="button" className={who === 'all' ? 'is-active' : ''} onClick={() => setWho('all')}>
          <Icon name="mail" size={16} /> Everyone
        </button>
        {cast?.cards.map((c) => (
          <button key={c._id} type="button" className={who === c.key ? 'is-active' : ''} onClick={() => setWho(c.key)}>
            <Avatar personaKey={c.key} mood={c.mood} size={20} /> {shortName(c.name)}
          </button>
        ))}
      </div>
      <Panel>{emails === undefined ? <Spinner /> : <EmailList emails={emails} now={now} showRecipient={who === 'all'} />}</Panel>
    </div>
  );
}
